import { Injectable, PLATFORM_ID, TransferState, WritableSignal, inject, makeStateKey, signal } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { ApiService } from "./api.service";
import { RequestHandlerParams } from "./types/api-service.types";
import { filter, tap } from "rxjs";
import { ApiResponse } from "./common/types";

// Carries the SSR-rendered menu to the client so it can be restored
// synchronously during bootstrap, instead of being fetched again (async) while
// the header renders 0 categories.
//
// This was introduced to stop a hydration crash — the header emitted N items on
// the server and 0 on the client at the synchronous hydration pass, and the
// node-count mismatch blew up with `Cannot set properties of null
// '__ngContext__'`. Hydration is no longer enabled (see app.config.ts), so the
// crash is gone, but the mechanism is worth keeping on its own merits: it saves
// a round-trip and stops the category bar appearing empty on first paint.
//
// Note this is Angular's TransferState, which works independently of hydration —
// unlike withHttpTransferCache, which shipped inside provideClientHydration and
// is therefore no longer active.
const MENU_STATE_KEY = makeStateKey<any[]>('menu-categories');

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private apiService = inject(ApiService);
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);

  // Restored synchronously on the client from the SSR TransferState payload, so
  // the header's category bar renders identically on the server and on the
  // client's first paint.
  menu: WritableSignal<any[]> = signal(this.#restoreMenuFromTransferState());

  private endpoints = {
    base: '/menu',
    categories: '/categories',
    territories: '/territories'
  };

  #restoreMenuFromTransferState(): any[] {
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(MENU_STATE_KEY)) {
      return this.transferState.get(MENU_STATE_KEY, []);
    }
    return [];
  }

  fetchMenuCategories(territory?: number) {
    // Don't wipe an already-populated menu (e.g. the TransferState-restored one
    // on the client) — clearing it post-hydration would flicker the nav and, if
    // it raced hydration, re-introduce the mismatch. Only clear when empty.
    if (!this.menu().length) this.menu.set([]);
    const endpoint = this.getMenuEndpoint(territory);

    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint,
      apiV3: true
    };

    return this.apiService.handleRequest<ApiResponse<{ categories: any[] }>>(params).pipe(
      filter((response: ApiResponse<{ categories: any[] }>) => !!response.data?.categories?.length),
      tap((response: ApiResponse<{ categories: any[] }>) => {
        this.menu.set(response.data.categories);
        // On the server, stash the result so the client restores it synchronously.
        if (!isPlatformBrowser(this.platformId)) {
          this.transferState.set(MENU_STATE_KEY, response.data.categories);
        }
      })
    );
  }

  private getMenuEndpoint(territory?: number) {
    const { base, territories, categories } = this.endpoints;

    let endpoint = `${base}`;
    // if (territory)
    //   endpoint += `${territories}/${territory}`;
    endpoint += categories;

    return endpoint;
  }
}
